// shared between server and client

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { Doc, Id } from "./_generated/dataModel";
import { LocationSubjectTag, PlantSubjectTag } from "./schema";

dayjs.extend(customParseFormat);

export function todayAsString() {
  return dayjs().format("YYYY-MM-DD");
}

/**
 *
 * @param date
 */
export function validateDateFormat(date: string) {
  const dateObj = dayjs(date, "YYYY-MM-DD", true);
  if (!dateObj.isValid()) {
    throw new Error("Date must be in ISO format");
  }
}


export const readingRoles: Doc<"userGardens">["role"][] = [
  "owner",
  "editor",
  "viewer",
];

export const writingRoles: Doc<"userGardens">["role"][] = ["owner", "editor"];

/**
 *
 * @param obj
 * @param key
 */
export function omitField<T, K extends keyof T>(obj: T, key: K): Omit<T, K> {
  const { [key]: _, ...rest } = obj; // eslint-disable-line @typescript-eslint/no-unused-vars
  return rest;
}

/**
 *
 * @param obj
 * @param keys
 */
export function omitFields<T, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> {
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).filter(
      ([key]) => !keys.includes(key as K)
    )
  ) as Omit<T, K>;
}


export function pickFields<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    result[key] = obj[key];
  });
  return result;
};


/**
 * Replace a field in an object with a new value and return a new object
 * @param obj
 * @param key
 * @param val
 */
export function replaceField<T, K extends keyof T, V>(
  obj: T,
  key: K,
  val: V
): Omit<T, K> & {
  [P in K]: V;
} {
  return { ...obj, [key]: val };
}


export type CoverPhoto = {
  file: StoredFile | UploadingFile;
  source: "entry" | "upload";
} | null;

export type StoredFile = { storageId: Id<"r2Photos">; url: string; };
export type UploadingFile = { storageId: null; url: string; };

export type HydratedPlant = Omit<
  Doc<"plants">, "locationId" | "r2CoverPhotoId"
> & {
  coverPhoto: StoredFile | null;
  location: { locationId: Id<"locations">; name: string; } | null;
};

export type HydratedLocation = Omit<Doc<"locations">, "r2CoverPhotoId"> & {
  depth: number;
  coverPhoto: StoredFile | null;
};

export type HydratedGarden = Omit<Doc<"gardens">, "r2CoverPhotoId"> & {
  role: Doc<"userGardens">["role"];
  coverPhoto: StoredFile | null;
};

export type HydratedEntry = Omit<Doc<"entries">, "r2PhotoIds" | "subjects"> & {
  photos: StoredFile[];
  subjects: (
    { doc: HydratedLocation; type: "location"; tags?: LocationSubjectTag[]; } |
    { doc: HydratedPlant; type: "plant"; tags?: PlantSubjectTag[]; }
  )[];
};
